import { setProgressBarCurrentStep } from '@avalara/bat-apps-shared/client/actions/progress-bar';
import { t } from '@avalara/bat-apps-shared/i18n';
import { SIcon } from '@avalara/skylab-sdk/react';

import type { Dispatch } from '@avalara/types/actions';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Pages } from '../../../../../types/offers';
import IframePaymentContainer from '../../../containers/iframe-payment-container';
import { updateProductAnalytics } from '../../../lib/side-effects/analytics';
import { goToNextOfferStep, goToPreviousOfferStep } from '../../../lib/side-effects/offer-flow';
import { stepNames } from '../../../lib/side-effects/progress-bar';
import { selectIsNonAdmin, selectState } from '../../../selectors';
import { isEmbeddedSelector } from '../../../selectors/offer';
import { setCurrentOfferPage } from '../../../store/offer';
import { setNeedToFetchCybersourceData } from '../../../store/system';

import { purchaseDeflectionWording } from '../../helpers';
import PaymentSelection from '../../shared/payment/payment-selection';
import SignInLink from '../sign-in-link';
import { FormContainer } from '../styled/forms';

const PaymentFormContainer = styled(FormContainer)`
  height: inherit;
`;

type Props = {
  isEmbedded?: boolean;
};

const OfferPaymentForm = (props: Props) => {
  const {
    account: { isLoggedIn, paymentMethods, selectedPaymentMethod },
    data: { isLoading },
    offer: { offerFlow },
  } = useSelector(selectState);

  const dispatch = useDispatch<Dispatch>();
  const history = useHistory();

  const isNonAdmin = useSelector(selectIsNonAdmin);
  const usingExistingPaymentMethod = !!selectedPaymentMethod;
  const hideIframe = usingExistingPaymentMethod || (isLoggedIn && isNonAdmin);
  const nextButtonAction = usingExistingPaymentMethod ? () => dispatch(goToNextOfferStep({ history })) : undefined;
  const isEmbedded = useSelector(isEmbeddedSelector);

  useEffect(() => {
    if (!hideIframe) {
      dispatch(setNeedToFetchCybersourceData(true));
    } else dispatch(setNeedToFetchCybersourceData(false));
  }, [hideIframe]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    dispatch(setCurrentOfferPage(Pages.Payment));
    dispatch(setProgressBarCurrentStep(stepNames.paymentMethod));
    dispatch(updateProductAnalytics('purchase_start', !!offerFlow));
  }, []);

  if (isLoggedIn && isNonAdmin) {
    return (
      <PaymentFormContainer isEmbedded={isEmbedded} className='pad-all-xl text-center'>
        <p className='text-lg font-semibold'>{purchaseDeflectionWording}</p>
      </PaymentFormContainer>
    );
  }

  const goBack = () => {
    dispatch(goToPreviousOfferStep(history));
  };

  return (
    <PaymentFormContainer id='payment-form-container' isEmbedded={isEmbedded}>
      <h1 data-ref='buy.payment.header'>
        {paymentMethods.length ? 'Select a payment method' : t('client.headings.add-a-credit-card')}
      </h1>

      {!isLoggedIn && <SignInLink />}

      {paymentMethods.length ? <PaymentSelection /> : null}

      {selectedPaymentMethod == null && (
        <IframePaymentContainer hideIframe={isLoading || hideIframe} adjustWidth={true} />
      )}

      {props.isEmbedded && (
        <div className='text-right'>
          <button type='button' className='secondary margin-right-lg' onClick={goBack}>
            {t('client.buttons.back')}
          </button>
          <button type='button' className='primary icon-trailing' onClick={nextButtonAction}>
            {t('client.buttons.next')}
            <SIcon name='arrow-circle-right' />
          </button>
        </div>
      )}
    </PaymentFormContainer>
  );
};

export default OfferPaymentForm;
